import { motion } from 'motion/react';

interface ProgressIndicatorProps {
  progress: number; // 0-1
  showLabel?: boolean;
  size?: 'sm' | 'md' | 'lg';
}

export function ProgressIndicator({ progress = 0, showLabel = false, size = 'md' }: ProgressIndicatorProps) {
  const clampedProgress = Math.max(0, Math.min(1, progress));
  const percentage = Math.round(clampedProgress * 100);

  const sizeClasses = {
    sm: 'w-16 h-16 text-xs',
    md: 'w-20 h-20 text-sm',
    lg: 'w-28 h-28 text-base',
  };

  const strokeWidth = { sm: 3, md: 4, lg: 5 }[size];
  const radius = { sm: 22, md: 26, lg: 34 }[size];
  const circumference = 2 * Math.PI * radius;

  return (
    <div className="flex items-center gap-3">
      <div className="relative" style={{ width: sizeClasses[size].split(' ')[0], height: sizeClasses[size].split(' ')[1] }}>
        <svg className="w-full h-full transform -rotate-90" viewBox="0 0 64 64">
          <circle
            className="text-border"
            cx="32"
            cy="32"
            r={radius}
            strokeWidth={strokeWidth}
            fill="none"
          />
          <motion.circle
            className="text-accent-primary"
            cx="32"
            cy="32"
            r={radius}
            strokeWidth={strokeWidth}
            strokeDasharray={circumference}
            strokeDashoffset={circumference * (1 - clampedProgress)}
            strokeLinecap="round"
            fill="none"
            initial={{ strokeDashoffset: circumference }}
            animate={{ strokeDashoffset: circumference * (1 - clampedProgress) }}
            transition={{ duration: 0.5, ease: [0.4, 0, 0.2, 1] }}
          />
        </svg>
        {showLabel && (
          <div className="absolute inset-0 flex items-center justify-center">
            <span className={`font-mono font-bold ${sizeClasses[size].split(' ')[2]}`}>
              {percentage}%
            </span>
          </div>
        )}
      </div>
      {showLabel && (
        <div className="text-text-secondary text-sm">
          Progress
        </div>
      )}
    </div>
  );
}